import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Cog, Disc3, Gauge, MonitorCog, Wrench, Zap } from 'lucide-react';
import { volumes, type VolumeIcon } from '../data/volumes';
const volumeIcons: Record<VolumeIcon, React.ComponentType<{
  size?: number;
  className?: string;
}>> = {
  wrench: Wrench,
  gauge: Gauge,
  disc: Disc3,
  zap: Zap,
  cog: Cog,
  monitor: MonitorCog
};

// Mirrors the colored book spines on the product mockup
const spineClasses = ['bg-spine-1', 'bg-spine-2', 'bg-spine-3', 'bg-spine-4', 'bg-spine-5', 'bg-spine-6'];
export function VolumesSection() {
  return <section className="py-24 px-4 bg-surface" data-id="element-306" data-mp="3pvkihxkdq2gz">
      <div className="max-w-6xl mx-auto" data-id="element-307" data-mp="3pwc90tz4da0e">
        <div className="text-center mb-16" data-id="element-308" data-mp="3l3sthrgvg4sy">
          <h2 className="text-3xl md:text-4xl font-display font-bold uppercase mb-4 text-primary" data-id="element-309" data-mp="3l2rdr9owuu4w">
            📚 THE 6-IN-1 AUTOMOTIVE TECHNOLOGY INSTRUCTOR READY SYSTEM
          </h2>
          <p className="text-xl text-primary/75" data-id="element-310" data-mp="2sevo2oxre2hm">
            <span className="text-secondary font-semibold" data-id="element-311" data-mp="29fic2m1l5s1l">150 Ready-to-Teach Lessons</span> ·{' '}
            <span className="text-secondary font-semibold" data-id="element-312" data-mp="2eelhw8pe42am">100% Visual Core Teaching Material</span>{' '}
            · Workshop-Ready Resources · Nothing to Build From Scratch
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-313" data-mp="2o4533l0ks88n">
          {volumes.map((volume, index) => {
          const Icon = volumeIcons[volume.icon];
          return <motion.article key={volume.number} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: index * 0.04,
            ease: [0.23, 1, 0.32, 1]
          }} className={`bg-white rounded-xl border ${volume.popular ? 'border-accent border-2 shadow-lg shadow-primary/10' : 'border-primary/15 shadow-sm shadow-primary/5'} p-8 pl-10 relative overflow-hidden flex flex-col h-full`} data-id="element-314" data-mp="1qiqymt8q675g" data-mpi={index}>
                <span className={`absolute left-0 inset-y-0 w-1.5 ${spineClasses[index % spineClasses.length]}`} aria-hidden="true" data-id="element-315" data-mp="1brztlvvj7pch" />
                {volume.popular && <div className="absolute top-6 right-[-38px] bg-accent text-primary font-bold text-xs py-1 px-10 rotate-45 shadow" data-id="element-316" data-mp="1kth859tajiz2">
                    ⭐ MOST POPULAR
                  </div>}

                <div className="flex items-center gap-4 mb-6 pr-12" data-id="element-317" data-mp="t2jg87yr02en">
                  <span className="w-14 h-14 rounded-lg bg-primary text-accent flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-318" data-mp="16t6gi4k5q0cu">
                    <Icon size={28} data-id="element-319" data-mp="ebu0qte9814z" />
                  </span>
                  <div data-id="element-320" data-mp="ea9avh2vx6dg">
                    <div className="text-secondary font-bold text-sm uppercase tracking-wider" data-id="element-321" data-mp="9dirajrvfsna">
                      {volume.number}
                    </div>
                    <h3 className="text-2xl font-display font-bold leading-tight mt-1 text-primary" data-id="element-322" data-mp="2kzb9a4m6pvs7">
                      {volume.title}
                    </h3>
                  </div>
                </div>

                <p className="text-primary/75 font-medium mb-6 italic border-l-2 border-accent pl-4" data-id="element-323" data-mp="2bxtkzyyminsf">
                  {volume.subtitle}
                </p>

                <div className="mb-6 space-y-2" data-id="element-324" data-mp="34pn1lrilhba5">
                  <h4 className="text-sm font-bold text-muted uppercase tracking-wider mb-2" data-id="element-325" data-mp="34mr3o36qymlp">
                    What Does This Solve for Your Teaching?
                  </h4>
                  {volume.problem.map(paragraph => <p key={paragraph} className="text-body text-sm leading-relaxed" data-id="element-326" data-mp="2zopjfexl4h8n">
                      {paragraph}
                    </p>)}
                </div>

                {volume.bulletsIntro && <p className="text-primary font-semibold text-sm mb-3" data-id="element-327" data-mp="1obwusd03c8wt">{volume.bulletsIntro}</p>}

                <ul className={`mb-6 flex-grow ${volume.bullets.length > 10 ? 'grid sm:grid-cols-2 gap-x-4 gap-y-3 content-start' : 'space-y-3'}`} data-id="element-328" data-mp="23la28fgt8qep">
                  {volume.bullets.map(bullet => <li key={bullet} className="flex items-start gap-3" data-id="element-329" data-mp="2miunzdoa38v3">
                      <CheckCircle2 size={18} className="text-secondary shrink-0 mt-0.5" data-id="element-330" data-mp="2mi1omxrnflmz" />
                      <span className="text-body text-sm" data-id="element-331" data-mp="2ihrct4h6e107">{bullet}</span>
                    </li>)}
                </ul>

                <div className="bg-surface border border-primary/15 rounded-lg p-4 mt-auto" data-id="element-332" data-mp="28j1ytmseh0p8">
                  <span className="text-primary font-bold block mb-1" data-id="element-333" data-mp="122zqn9amhpzc">Result:</span>
                  <p className="text-body text-sm" data-id="element-334" data-mp="120wus8msh2mp">{volume.result}</p>
                  {volume.resultChain && <p className="text-white font-display font-bold tracking-wide text-sm bg-primary rounded-md px-3 py-2 my-2" data-id="element-335" data-mp="mzzr6m60v96c">
                      {volume.resultChain}
                    </p>}
                  {volume.resultAfter && <p className="text-body text-sm" data-id="element-336" data-mp="wx4e4o7oz8o7">{volume.resultAfter}</p>}
                </div>
              </motion.article>;
        })}
        </div>
      </div>
    </section>;
}